import { CircleAlert, CircleCheck } from 'lucide-react'

import { Tooltip } from '~/components/component-library'
import {
  PillProps,
  VariantConfig,
} from '~/components/component-library/Pill/types'
import { cn } from '~/utils/tailwind-utils'

export type { PillProps } from '~/components/component-library/Pill/types'

export function Pill({
  variant = 'default',
  size = 'base',
  label,
  tooltipMessage,
  icon,
}: PillProps) {
  const { className, icon: VariantIcon } = variantConfig[variant]
  const Icon = icon ?? VariantIcon

  const content = (
    <div className={cn(baseClassName, sizeClassName[size], className)}>
      {Icon && <Icon />}
      <span className="whitespace-nowrap">{label}</span>
    </div>
  )

  if (tooltipMessage) {
    return <Tooltip content={tooltipMessage}>{content}</Tooltip>
  } else {
    return content
  }
}

const baseClassName =
  'inline-flex w-fit items-center rounded-full ring-1 ring-inset leading-none whitespace-nowrap select-none cursor-default'

const sizeClassName: Record<NonNullable<PillProps['size']>, string> = {
  base: 'px-2.5 py-0.5 text-sm [&>svg]:size-4 [&>svg]:-ml-1.5 [&>svg]:mr-1',
  small: 'px-2 py-0.5 text-xs [&>svg]:size-3.5 [&>svg]:-ml-1 [&>svg]:mr-1',
}

const variantConfig: Record<
  NonNullable<PillProps['variant']>,
  VariantConfig
> = {
  default: {
    className:
      'ring-gray-300 bg-gray-100 text-secondary dark:ring-gray-700 dark:bg-gray-800',
  },
  success: {
    className:
      'text-green-500 bg-green-50 ring-green-500 dark:bg-green-900/50 dark:ring-green-800 dark:text-green-400',
    icon: () => <CircleCheck />,
  },
  danger: {
    className:
      'text-error bg-red-50 ring-red-500 dark:bg-red-900/50 dark:ring-red-800 dark:text-red-400',
    icon: () => <CircleAlert />,
  },
  warning: {
    className:
      'text-yellow-600 bg-yellow-50 ring-yellow-500 dark:bg-yellow-900/50 dark:ring-yellow-700 dark:text-yellow-400',
    icon: () => <CircleAlert />,
  },
}
